/**
 * Dialog — modal applicatif glass (R13).
 *
 * RÈGLE D'OR du projet : AUCUNE modal navigateur (window.alert/confirm/prompt)
 * dans le code. Capacitor (APK) ne les supporte pas correctement et elles
 * cassent la cohérence visuelle. Utilise ce composant à la place.
 *
 * Variantes :
 *   - <Dialog open onClose ...>  : modal contrôlé bas niveau (titre + body + footer)
 *   - <ConfirmDialog ...>        : pattern usuel (confirmation oui/non + danger optionnel)
 *   - <PromptDialog ...>         : saisie d'un texte (remplace window.prompt)
 *
 * Hook associé :
 *   - useConfirmDialog() → { confirm, prompt, render } — voir lib/use-confirm-dialog.tsx
 *
 * Accessibilité :
 *   - role="dialog", aria-modal="true", aria-labelledby titre
 *   - focus trap simple (focus initial + restitution à fermeture)
 *   - Échap pour fermer (mappé sur onClose)
 *   - Touch targets ≥ 44px sur les boutons
 *
 * R13.6 (21 mai 2026) — Fix UX critique :
 *   - Le clic sur le backdrop ne ferme PLUS la modale par défaut. Cause du fix :
 *     un Ctrl+V qui dépassait la zone de la modale annulait tout le travail en
 *     cours (perte de données). C'est le mode par défaut désormais. Si on veut
 *     l'ancien comportement (close au backdrop click), passer `closeOnBackdropClick`.
 *   - Pour fermer la modale, l'utilisateur a : la croix en haut à droite, le
 *     bouton "Annuler" du footer s'il existe, ou la touche Échap.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { Button } from './index';

// ============================================================================
// Dialog bas niveau
// ============================================================================

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** Cacher la croix de fermeture (utile pour les confirmations bloquantes) */
  hideCloseButton?: boolean;
  /** Largeur max du dialog. Default 'md' (28rem). */
  size?: 'sm' | 'md' | 'lg';
  /** Variante icone (purement visuelle). */
  variant?: 'info' | 'warning' | 'danger' | 'success' | 'neutral';
  /**
   * R13.6 — Si true, un clic sur le backdrop ferme la modale.
   * Default false : protection contre la perte de données (Ctrl+V qui dépasse).
   * Fermeture possible via croix, bouton Annuler, ou Échap.
   */
  closeOnBackdropClick?: boolean;
}

const SIZE_CLS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
} as const;

const VARIANT_ICON = {
  info: { Icon: Info, color: 'text-tech-fg', bg: 'bg-tech/15' },
  warning: { Icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/15' },
  danger: { Icon: AlertCircle, color: 'text-urgent', bg: 'bg-urgent/15' },
  success: { Icon: CheckCircle2, color: 'text-success', bg: 'bg-success/15' },
  neutral: { Icon: Info, color: 'text-ink-muted', bg: 'bg-surface/60' },
} as const;

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  hideCloseButton,
  size = 'md',
  variant = 'neutral',
  closeOnBackdropClick = false,                                     // R13.6 — default false (sécurité contre perte de données)
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<Element | null>(null);

  // Refs miroir vers les props lues DANS l'effet focus/Échap.
  // Sans ça, l'effet aurait `onClose`/`hideCloseButton` en dépendances : or les
  // parents passent presque toujours un `onClose` recréé à chaque render →
  // l'effet se relancerait à CHAQUE frappe clavier et arracherait le focus du
  // champ en cours de saisie vers la croix de fermeture. L'effet ne dépend donc
  // QUE de `open` ; le handler Échap lit la valeur courante via la ref.
  const onCloseRef = useRef(onClose);
  const hideCloseButtonRef = useRef(hideCloseButton);
  useEffect(() => {
    onCloseRef.current = onClose;
    hideCloseButtonRef.current = hideCloseButton;
  });

  // Gestion focus + Échap + scroll lock — UNIQUEMENT au changement de `open`.
  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement;

    // Auto-focus le dialog (ou son premier élément focusable)
    const t = setTimeout(() => {
      const first = dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      first?.focus();
    }, 50);

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !hideCloseButtonRef.current) {
        e.preventDefault();
        onCloseRef.current();
      }
    }
    document.addEventListener('keydown', onKey);

    // Lock scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restitution focus
      (previousFocus.current as HTMLElement | null)?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const { Icon, color, bg } = VARIANT_ICON[variant];

  // Rendu dans un portal sur document.body : sans cela, un ancêtre avec
  // transform / filter / backdrop-filter (ex. les cartes ServiceCard) crée un
  // contexte d'empilement qui « piège » le position:fixed → la modale passe
  // sous les bandeaux et devient inutilisable. Le portal l'ancre au viewport.
  return createPortal((
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      {...(description ? { 'aria-describedby': 'dialog-description' } : {})}
    >
      {/* Backdrop — R13.6 : ne ferme PLUS par défaut. Le clic est ignoré
          (sauf si closeOnBackdropClick = true). Évite la perte de travail
          quand un Ctrl+V dépasse la zone de la modale. */}
      {closeOnBackdropClick && !hideCloseButton ? (
        <button
          type="button"
          aria-label="Fermer la fenêtre"
          onClick={onClose}
          className="absolute inset-0 bg-bg/70 backdrop-blur-sm cursor-pointer"
          tabIndex={-1}
        />
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-bg/70 backdrop-blur-sm pointer-events-none"
        />
      )}

      {/* Dialog */}
      <div
        ref={dialogRef}
        className={`relative flex max-h-[92dvh] w-full flex-col ${SIZE_CLS[size]} rounded-t-[0.875rem] border border-border bg-surface/95 backdrop-blur-glass shadow-glass-hi animate-fade-in sm:max-h-[90vh] sm:rounded-r-xl`}
      >
        {/* Header */}
        <header className="flex flex-shrink-0 items-start gap-3 border-b border-border p-4 sm:p-5">
          <div
            className={`inline-grid h-10 w-10 place-items-center rounded-r-lg ${bg} ${color} flex-shrink-0`}
            aria-hidden="true"
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="dialog-title" className="font-display text-lg font-semibold tracking-r-snug text-ink">
              {title}
            </h2>
            {description && (
              <p id="dialog-description" className="mt-1 text-sm text-ink-muted leading-relaxed">
                {description}
              </p>
            )}
          </div>
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="inline-flex h-10 w-10 items-center justify-center rounded-r-md text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </header>

        {/* Body (scrollable) */}
        {children && <div className="flex-1 overflow-y-auto p-4 sm:p-5">{children}</div>}

        {/* Footer (fixe) */}
        {footer && (
          <footer className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-surface/40 backdrop-blur-glass px-4 py-3 sm:px-5 sm:rounded-b-[0.875rem]">
            {footer}
          </footer>
        )}
      </div>
    </div>
  ), document.body);
}

// ============================================================================
// ConfirmDialog — remplace window.confirm() avec danger optionnel
// ============================================================================

export interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Si true, le bouton confirm est en variant danger (rouge). */
  danger?: boolean;
  /** Loading sur le bouton confirm (action async en cours). */
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  danger,
  loading,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onCancel}
      title={title}
      description={description}
      variant={danger ? 'danger' : 'warning'}
      hideCloseButton={loading}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            size="md"
            onClick={onConfirm}
            loading={loading}
            autoFocus
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}

// ============================================================================
// PromptDialog — remplace window.prompt() avec un textarea ou un input
// ============================================================================

export interface PromptDialogProps {
  open: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  title: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  /** Si true, multi-ligne (textarea). Sinon input simple. */
  multiline?: boolean;
  /** Longueur maximale (count visible si présent). */
  maxLength?: number;
  /** Champ requis ? */
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
}

const INPUT_CLS =
  'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function PromptDialog({
  open,
  onSubmit,
  onCancel,
  title,
  description,
  placeholder,
  defaultValue,
  multiline,
  maxLength,
  required,
  confirmLabel = 'Valider',
  cancelLabel = 'Annuler',
  loading,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue ?? '');

  useEffect(() => {
    if (open) setValue(defaultValue ?? '');
  }, [open, defaultValue]);

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (required && !value.trim()) return;
    onSubmit(value);
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onCancel}
      title={title}
      description={description}
      variant="info"
      hideCloseButton={loading}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => handleSubmit()}
            loading={loading}
            disabled={loading || (required && !value.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={4}
            required={required}
            autoFocus
            className={INPUT_CLS}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            required={required}
            autoFocus
            className={`h-10 ${INPUT_CLS}`}
          />
        )}
        {maxLength && (
          <div className="mt-1 text-right text-xs text-ink-subtle tabular-nums">
            {value.length} / {maxLength}
          </div>
        )}
      </form>
    </Dialog>
  );
}
